import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "../Trading/AddCurrencyPairPage.css";

const RETURN_TYPE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "one_time", label: "One Time" },
];

const PAYOUT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const WITH_PRINCIPAL_AMOUNT_OPTIONS = [
  { value: "false", label: "Without principal" },
  { value: "true", label: "With principal" },
];

const AddStakingPackagePage = () => {
  const navigate = useNavigate();
  const [coinsList, setCoinsList] = useState([]);
  const [coinsLoading, setCoinsLoading] = useState(true);
  const [form, setForm] = useState({
    currencyId: "",
    duration_days: "",
    return_percentage: "",
    returnPercenatgeShowtoUser: "",
    return_type: "daily",
    min_amount: "",
    cancellation_fee: "",
    level1_commission: "",
    level1_instant_bonus: "",
    level2_commission: "",
    level3_commission: "",
    level4_commission: "",
    level5_commission: "",
    referral_commission_type: "daily",
    payout_status: "active",
    withPrincipalAmount: "false",
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successModal, setSuccessModal] = useState(null);

  const fetchCoins = useCallback(async () => {
    setCoinsLoading(true);
    const res = await AdminService.getCoinsList();
    setCoinsLoading(false);
    if (res?.success && Array.isArray(res.data)) {
      setCoinsList(res.data);
    } else {
      setCoinsList([]);
    }
  }, []);

  useEffect(() => {
    fetchCoins();
  }, [fetchCoins]);

  const updateForm = (key, value) => {
    setSubmitError("");
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitError("");

    const duration_days = Number(form.duration_days);
    const return_percentage = Number(form.return_percentage);
    const returnPercenatgeShowtoUser = Number(form.returnPercenatgeShowtoUser);
    const min_amount = Number(form.min_amount);
    const cancellation_fee =
      form.cancellation_fee === "" || form.cancellation_fee == null
        ? 0
        : Number(form.cancellation_fee);
    const level1_commission = Number(form.level1_commission);
    const level1_instant_bonus = Number(form.level1_instant_bonus);
    const level2_commission = Number(form.level2_commission);
    const level3_commission = Number(form.level3_commission);
    const level4_commission = Number(form.level4_commission);
    const level5_commission = Number(form.level5_commission);

    if (!form.currencyId?.trim()) {
      setSubmitError("Select a currency.");
      return;
    }
    if (!Number.isFinite(duration_days) || duration_days <= 0) {
      setSubmitError("Duration (days) must be greater than 0.");
      return;
    }
    if (!Number.isFinite(return_percentage) || return_percentage <= 0) {
      setSubmitError("Return (%) must be greater than 0.");
      return;
    }
    if (!Number.isFinite(returnPercenatgeShowtoUser) || returnPercenatgeShowtoUser <= 0) {
      setSubmitError("Return % show to user must be greater than 0.");
      return;
    }
    const allowedReturnTypes = RETURN_TYPE_OPTIONS.map((o) => o.value);
    if (!allowedReturnTypes.includes(form.return_type)) {
      setSubmitError("Select a valid return type.");
      return;
    }
    if (!Number.isFinite(min_amount) || min_amount <= 0) {
      setSubmitError("Minimum amount must be greater than 0.");
      return;
    }
    if (!Number.isFinite(cancellation_fee) || cancellation_fee < 0) {
      setSubmitError("Cancellation fee must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level1_commission) || level1_commission < 0) {
      setSubmitError("Level 1 commission must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level1_instant_bonus) || level1_instant_bonus < 0) {
      setSubmitError("Level 1 instant bonus must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level2_commission) || level2_commission < 0) {
      setSubmitError("Level 2 commission must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level3_commission) || level3_commission < 0) {
      setSubmitError("Level 3 commission must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level4_commission) || level4_commission < 0) {
      setSubmitError("Level 4 commission must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level5_commission) || level5_commission < 0) {
      setSubmitError("Level 5 commission must be 0 or greater.");
      return;
    }
    const allowedReferralCommissionTypes = RETURN_TYPE_OPTIONS.map((o) => o.value);
    if (!allowedReferralCommissionTypes.includes(form.referral_commission_type)) {
      setSubmitError("Select a valid referral commission type.");
      return;
    }
    const allowedPayoutStatuses = PAYOUT_STATUS_OPTIONS.map((o) => o.value);
    if (!allowedPayoutStatuses.includes(form.payout_status)) {
      setSubmitError("Select a valid payout status.");
      return;
    }
    const allowedWithPrincipal = WITH_PRINCIPAL_AMOUNT_OPTIONS.map((o) => o.value);
    if (!allowedWithPrincipal.includes(form.withPrincipalAmount)) {
      setSubmitError("Select a valid principal amount option.");
      return;
    }

    const body = {
      currencyId: form.currencyId.trim(),
      duration_days,
      return_percentage,
      returnPercenatgeShowtoUser,
      return_type: form.return_type,
      min_amount,
      cancellation_fee,
      level1_commission,
      level1_instant_bonus,
      level2_commission,
      level3_commission,
      level4_commission,
      level5_commission,
      referral_commission_type: form.referral_commission_type,
      payout_status: form.payout_status,
      withPrincipalAmount: form.withPrincipalAmount === "true",
    };

    setSubmitLoading(true);
    try {
      const res = await AdminService.createEarningPackage(body);
      if (res?.success) {
        setSuccessModal({
          message: res.message || "Earning package created successfully.",
          ...body,
        });
      } else {
        setSubmitError(res?.message || "Could not create package.");
      }
    } catch (err) {
      console.error(err);
      setSubmitError("Could not create package.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessModal(null);
    navigate("/dashboard/staking-packages");
  };

  const selectedCoin = coinsList.find(
    (c) => (c._id || c.id) === form.currencyId
  );

  return (
    <main className="dashboard-main dashboard-subpage addpair-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/staking-packages">Staking</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Add Package</span>
          </p>
          <h1 className="dashboard-hero__title">Add Staking Package</h1>
          <p className="dashboard-hero__subtitle">
            Set currency, term, return %, return cadence (daily/monthly/yearly/one time), min amount, and optional early cancellation fee
          </p>
        </div>
        <div className="dashboard-hero__icon addpair-hero__icon">
          <i className="fas fa-coins" />
        </div>
      </section>

      <div className="dashboard-body addpair-body">
        <div className="addpair-card">
          {submitError && (
            <p
              style={{
                color: "#dc2626",
                margin: "0 0 1rem",
                padding: "0.5rem 0.75rem",
                background: "rgba(220, 38, 38, 0.08)",
                borderRadius: 8,
              }}
            >
              {submitError}
            </p>
          )}
          <form onSubmit={handleSubmit} className="addpair-form">
            <div className="addpair-config-grid">
              <div className="cp-field">
                <label>
                  Currency <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-gem" />
                  <select
                    value={form.currencyId}
                    onChange={(e) => updateForm("currencyId", e.target.value)}
                    required
                    disabled={coinsLoading}
                  >
                    <option value="">
                      {coinsLoading ? "Loading currencies…" : "Select currency"}
                    </option>
                    {[...coinsList]
                      .filter((c) => c._id || c.id)
                      .sort((a, b) =>
                        (a.short_name || "").localeCompare(b.short_name || "")
                      )
                      .map((c) => {
                        const id = c._id || c.id;
                        const label = c.short_name || c.name || id;
                        return (
                          <option key={id} value={id}>
                            {label}
                            {c.name && c.name !== label ? ` — ${c.name}` : ""}
                          </option>
                        );
                      })}
                  </select>
                </div>
              </div>
              <div className="cp-field">
                <label>
                  Duration (days) <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-calendar-day" />
                  <input
                    type="number"
                    step="1"
                    min={1}
                    placeholder="30"
                    value={form.duration_days}
                    onChange={(e) => updateForm("duration_days", e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="cp-field">
                <label>
                  Return (%) <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-percent" />
                  <input
                    type="number"
                    step="0.01"
                    min={0.01}
                    placeholder="2"
                    value={form.return_percentage}
                    onChange={(e) =>
                      updateForm("return_percentage", e.target.value)
                    }
                    required
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  e.g. 2 for 2%
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Return % show to user <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-eye" />
                  <input
                    type="number"
                    step="0.01"
                    min={0.01}
                    placeholder="2"
                    value={form.returnPercenatgeShowtoUser}
                    onChange={(e) =>
                      updateForm("returnPercenatgeShowtoUser", e.target.value)
                    }
                    required
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Return percentage displayed to users (e.g. 2 for 2%).
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Return type <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-clock" />
                  <select
                    value={form.return_type}
                    onChange={(e) => updateForm("return_type", e.target.value)}
                    required
                  >
                    {RETURN_TYPE_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  How often return % applies (daily, monthly, yearly, or one time).
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Min amount <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-coins" />
                  <input
                    type="number"
                    step="any"
                    min={0.00000001}
                    placeholder="10"
                    value={form.min_amount}
                    onChange={(e) => updateForm("min_amount", e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="cp-field">
                <label>Cancellation fee</label>
                <div className="cp-input-wrap">
                  <i className="fas fa-ban" />
                  <input
                    type="number"
                    step="any"
                    min={0}
                    placeholder="0"
                    value={form.cancellation_fee}
                    onChange={(e) => updateForm("cancellation_fee", e.target.value)}
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Fee charged when a user cancels early (same unit as currency). Use 0 if none.
                </small>
              </div>
              <div className="cp-field">
                <label>Referral commission type</label>
                <div className="cp-input-wrap">
                  <i className="fas fa-sync-alt" />
                  <select
                    value={form.referral_commission_type}
                    onChange={(e) =>
                      updateForm("referral_commission_type", e.target.value)
                    }
                  >
                    {RETURN_TYPE_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  How often referral commission is paid (daily, monthly, yearly, or one time).
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Level 1 commission (%) <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-user-friends" />
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="10"
                    value={form.level1_commission}
                    onChange={(e) => updateForm("level1_commission", e.target.value)}
                    required
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Referral commission for level 1 (e.g. 10 for 10%).
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Level 1 instant bonus (%) <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-bolt" />
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="0"
                    value={form.level1_instant_bonus}
                    onChange={(e) => updateForm("level1_instant_bonus", e.target.value)}
                    required
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Instant bonus for level 1 referrals (e.g. 5 for 5%). Use 0 if none.
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Level 2 commission (%) <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-users" />
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="5"
                    value={form.level2_commission}
                    onChange={(e) => updateForm("level2_commission", e.target.value)}
                    required
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Referral commission for level 2 (e.g. 5 for 5%).
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Level 3 commission (%) <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-sitemap" />
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="2"
                    value={form.level3_commission}
                    onChange={(e) => updateForm("level3_commission", e.target.value)}
                    required
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Referral commission for level 3 (e.g. 2 for 2%).
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Level 4 commission (%) <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-sitemap" />
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="2"
                    value={form.level4_commission}
                    onChange={(e) => updateForm("level4_commission", e.target.value)}
                    required
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Referral commission for level 4 (e.g. 2 for 2%).
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Level 5 commission (%) <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-sitemap" />
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="1"
                    value={form.level5_commission}
                    onChange={(e) => updateForm("level5_commission", e.target.value)}
                    required
                  />
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Referral commission for level 5 (e.g. 1 for 1%).
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Principal amount <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-wallet" />
                  <select
                    value={form.withPrincipalAmount}
                    onChange={(e) => updateForm("withPrincipalAmount", e.target.value)}
                    required
                  >
                    {WITH_PRINCIPAL_AMOUNT_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  With principal includes staked amount in payout; without principal pays returns only.
                </small>
              </div>
              <div className="cp-field">
                <label>
                  Payout status <span className="required">*</span>
                </label>
                <div className="cp-input-wrap">
                  <i className="fas fa-hand-holding-usd" />
                  <select
                    value={form.payout_status}
                    onChange={(e) => updateForm("payout_status", e.target.value)}
                    required
                  >
                    {PAYOUT_STATUS_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                  Whether staking payouts are active or paused for this package.
                </small>
              </div>
            </div>
            <div className="addpair-actions">
              <Link
                to="/dashboard/staking-packages"
                className="cp-btn cp-btn--outline"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="cp-btn cp-btn--primary"
                disabled={submitLoading || coinsLoading || !coinsList.length}
              >
                {submitLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Creating…
                  </>
                ) : (
                  <>
                    <i className="fas fa-check" /> Create package
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {successModal && (
        <div className="addpair-success-overlay" onClick={handleSuccessClose}>
          <div
            className="addpair-success-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="addpair-success-icon">
              <i className="fas fa-check-circle" />
            </div>
            <h3>Package created</h3>
            <p>{successModal.message}</p>
            <div className="addpair-success-details">
              <div className="addpair-success-row">
                <span>Currency</span>
                <strong>
                  {selectedCoin?.short_name || selectedCoin?.name || "—"}
                </strong>
              </div>
              <div className="addpair-success-row">
                <span>Duration</span>
                <strong>{successModal.duration_days} days</strong>
              </div>
              <div className="addpair-success-row">
                <span>Return</span>
                <strong>{successModal.return_percentage}%</strong>
              </div>
              <div className="addpair-success-row">
                <span>Return % show to user</span>
                <strong>{successModal.returnPercenatgeShowtoUser}%</strong>
              </div>
              <div className="addpair-success-row">
                <span>Return type</span>
                <strong>
                  {RETURN_TYPE_OPTIONS.find((o) => o.value === successModal.return_type)
                    ?.label ?? successModal.return_type}
                </strong>
              </div>
              <div className="addpair-success-row">
                <span>Min amount</span>
                <strong>{successModal.min_amount}</strong>
              </div>
              <div className="addpair-success-row">
                <span>Cancellation fee</span>
                <strong>{successModal.cancellation_fee}</strong>
              </div>
              <div className="addpair-success-row">
                <span>Level 1 commission</span>
                <strong>{successModal.level1_commission}%</strong>
              </div>
              <div className="addpair-success-row">
                <span>Level 1 instant bonus</span>
                <strong>{successModal.level1_instant_bonus}%</strong>
              </div>
              <div className="addpair-success-row">
                <span>Level 2 commission</span>
                <strong>{successModal.level2_commission}%</strong>
              </div>
              <div className="addpair-success-row">
                <span>Level 3 commission</span>
                <strong>{successModal.level3_commission}%</strong>
              </div>
              <div className="addpair-success-row">
                <span>Level 4 commission</span>
                <strong>{successModal.level4_commission}%</strong>
              </div>
              <div className="addpair-success-row">
                <span>Level 5 commission</span>
                <strong>{successModal.level5_commission}%</strong>
              </div>
              <div className="addpair-success-row">
                <span>Referral commission type</span>
                <strong>
                  {RETURN_TYPE_OPTIONS.find(
                    (o) => o.value === successModal.referral_commission_type
                  )?.label ?? successModal.referral_commission_type}
                </strong>
              </div>
              <div className="addpair-success-row">
                <span>Principal amount</span>
                <strong>
                  {WITH_PRINCIPAL_AMOUNT_OPTIONS.find(
                    (o) => o.value === String(successModal.withPrincipalAmount)
                  )?.label ??
                    (successModal.withPrincipalAmount ? "With principal" : "Without principal")}
                </strong>
              </div>
              <div className="addpair-success-row">
                <span>Payout status</span>
                <strong>
                  {PAYOUT_STATUS_OPTIONS.find((o) => o.value === successModal.payout_status)
                    ?.label ?? successModal.payout_status}
                </strong>
              </div>
            </div>
            <button
              type="button"
              className="cp-btn cp-btn--primary"
              onClick={handleSuccessClose}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default AddStakingPackagePage;
